<template>
  <div class="ai-report-stream">
    <!-- 1. 思考动效（初始状态 + 流式进行中但尚未收到正文） -->
    <div v-if="!fullMarkdown && !streamError" class="thinking-wrapper">
      <div class="thinking-spinner"></div>
      <div class="thinking-content">
        <div class="thinking-title">{{ statusTitle }}</div>
        <div class="thinking-detail">{{ statusDetail }}</div>
        <div class="thinking-dots">
          <span></span><span></span><span></span>
        </div>
      </div>
    </div>

    <!-- 2. 报告正文（已有内容时渲染） -->
    <section v-if="fullMarkdown" class="section-stack">
      <!-- 行情概述 -->
      <article id="overview" class="overview" v-if="overviewContent">
        <h2>行情概述</h2>
        <p v-html="overviewContent"></p>
      </article>

      <!-- 主要章节 -->
      <section id="logicList" class="section-stack">
        <details
          v-for="(section, index) in sections"
          :key="index"
          class="logic-card"
          open
        >
          <summary>
            <span class="icon"><img :src="section.icon" alt="" /></span>
            <h3 class="summary-title">{{ section.title }}</h3>
            <span class="chevron"></span>
          </summary>
          <div class="logic-body">
            <p
              v-for="(paragraph, idx) in section.intro"
              :key="idx"
              class="section-intro"
              v-html="paragraph"
            ></p>
            <p
              v-for="(item, idx) in section.items"
              :key="idx"
              class="numbered-item"
            >
              <span class="item-marker">{{ idx + 1 }}.</span>
              <span class="item-copy" v-html="item"></span>
            </p>
          </div>
        </details>
      </section>

      <!-- 风险提醒 -->
      <article
        v-if="riskSection"
        id="riskCard"
        class="risk-card"
        :class="{ open: isRiskOpen }"
      >
        <button class="risk-head" @click="toggleRisk">
          <span class="icon">
            <img src="https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-risk.svg" alt="" />
          </span>
          <h3>{{ riskSection.title }}</h3>
          <span class="chevron"></span>
        </button>
        <div id="riskList" class="risk-list" :style="riskListStyle">
          <div v-for="(item, idx) in riskSection.items" :key="idx">
            <p class="risk-item" v-html="item"></p>
            <span
              v-if="idx < riskSection.items.length - 1"
              class="risk-divider"
            ></span>
          </div>
        </div>
      </article>

      <!-- 3. 打字机光标（生成中显示） -->
      <span v-if="showCursor" class="cursor">▌</span>
    </section>

    <!-- 错误提示与重试 -->
    <div v-if="streamError" class="stream-error">
      <p>{{ streamError }}</p>
      <button @click="reGenerate">重新生成</button>
    </div>
  </div>
</template>

<script>
import md5 from 'md5';

export default {
  name: 'AiReportStream',
  props: {
    code: { type: String, required: true },
    name: { type: String, required: true },
    uId: { type: String, required: true },
    // 新增：初始 Markdown 内容（如果有缓存）
    initialMarkdown: { type: String, default: '' }
  },
  data() {
    return {
      // ---------- 流式状态 ----------
      abortController: null,
      isStreaming: false,
      fullMarkdown: '',
      streamError: null,

      // ---------- 渲染数据 ----------
      overviewContent: '',
      sections: [],
      riskSection: null,
      isRiskOpen: true,

      // ---------- 光标 ----------
      showCursor: false,

      // ---------- 思考步骤 ----------
      thinkingSteps: [
        { title: '正在分析', detail: '正在识别股票并建立本次诊股任务' },
        { title: '正在检索资料', detail: '正在获取最新行情、公告与行业信息' },
        { title: '正在核对行情', detail: '正在分析涨跌、成交量与异常波动' },
        { title: '正在梳理行情主线', detail: '正在判断业绩、行业、资金与题材影响' },
        { title: '正在交叉验证事实', detail: '正在区分已发生事实、市场预期与题材情绪' },
        { title: '正在整理后市信号', detail: '正在归纳催化条件、趋势分水岭与风险提示' },
        { title: '马上为您呈现诊断结果', detail: '' },
      ],
      stepIndex: 0,
      statusTitle: '正在分析',
      statusDetail: '正在识别股票并建立本次诊股任务',
      thinkingTimer: null,
      firstContentReceived: false,
    };
  },
  computed: {
    riskListStyle() {
      return this.isRiskOpen
        ? { height: 'auto', opacity: 1 }
        : { height: 0, opacity: 0, overflow: 'hidden' };
    },
  },
  mounted() {
    if (this.initialMarkdown) {
      // 缓存模式：直接渲染，不发起请求
      this.fullMarkdown = this.initialMarkdown;
      this.parseAndRender(this.fullMarkdown);
      this.isStreaming = false;
      this.showCursor = false;
      this.statusTitle = '已缓存诊股报告';
      this.statusDetail = `生成于历史时间`;
    } else {
      // 正常模式：发起流式请求
      this.startStream();
    }
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    // ---------- 启动流式请求 ----------
    async startStream() {
      this.cleanup();

      // 重置状态
      this.isStreaming = true;
      this.streamError = null;
      this.fullMarkdown = '';
      this.overviewContent = '';
      this.sections = [];
      this.riskSection = null;
      this.showCursor = false;
      this.firstContentReceived = false;
      this.stepIndex = 0;
      this.statusTitle = this.thinkingSteps[0].title;
      this.statusDetail = this.thinkingSteps[0].detail;

      // 启动思考步骤定时器（1.5秒切换）
      this.startThinkingTimer();

      // 计算 time 和 sign
      const time = String(Math.floor(Date.now() / 1000));
      const sign = md5('20160316|' + time);

      const body = {
        question: [
          {
            role: 'user',
            content: `${this.name}（${this.code}）近期走势、异动原因及后市观察`,
          },
        ],
        stream: true,
        time,
        sign,
        u_id:this.uId,
      };

      this.abortController = new AbortController();

      let _host = location.host, _currentDefault;
      if(_host.indexOf("dev") > -1 || _host.indexOf("192") > -1) {
          _currentDefault = "dev"
      } else if(_host.indexOf("beta") > -1) {
          _currentDefault = "beta"
      } else {
          _currentDefault = ""
      }

      try {
        const response = await fetch(`https://app${_currentDefault}.aigupiao.com/sse/ai/stock_diagnosis_v2`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            let dataStr = line;
            if (line.startsWith('data:')) {
              dataStr = line.slice(5).trim();
            }

            if (dataStr === '[DONE]') {
              this.isStreaming = false;
              this.showCursor = false;
              this.stopThinkingTimer();
              this.parseAndRender(this.fullMarkdown);
              this.$emit('stream-done');
              continue;
            }

            if (dataStr) {
              try {
                const data = JSON.parse(dataStr);
                this.handleSSEMessage(data);
              } catch (e) {
                console.warn('非 JSON 数据:', dataStr);
              }
            }
          }
        }

        // 正常流结束
        if (this.isStreaming) {
          this.isStreaming = false;
          this.showCursor = false;
          this.stopThinkingTimer();
          this.parseAndRender(this.fullMarkdown);
          this.$emit('stream-done');
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('请求已中止');
        } else {
          this.streamError = error.message || '网络异常';
          this.$emit('stream-error', this.streamError);
        }
      } finally {
        this.isStreaming = false;
        this.abortController = null;
        this.stopThinkingTimer();
      }
    },

    // ---------- 处理单条 SSE 消息 ----------
    handleSSEMessage(data) {
      if (data.object === 'diagnosis.meta') {
        console.log('Meta:', data.data);
        return;
      }

      if (data.object === 'chat.completion.chunk' && data.choices && data.choices.length > 0) {
        const delta = data.choices[0].delta;
        if (!delta) return;

        // 思考内容（暂不展示）
        if (delta.reasoning_content) {
          return;
        }

        // 正文内容
        if (delta.content) {
          if (!this.firstContentReceived) {
            this.firstContentReceived = true;
            this.stopThinkingTimer();
            this.statusTitle = '正在生成分析结果';
            this.statusDetail = '行情核验已完成，正在组织核心主线与风险提示';
            this.showCursor = true;
          }

          this.fullMarkdown += delta.content;
          this.$nextTick(() => {
            this.parseAndRender(this.fullMarkdown);
          });
          return;
        }
      }

      if (data.error) {
        this.streamError = data.error.message || data.error || '未知错误';
        this.$emit('stream-error', this.streamError);
        if (this.abortController) this.abortController.abort();
        return;
      }

      // 降级处理
      if (data.content) {
        if (!this.firstContentReceived) {
          this.firstContentReceived = true;
          this.stopThinkingTimer();
          this.statusTitle = '正在生成分析结果';
          this.statusDetail = '行情核验已完成，正在组织核心主线与风险提示';
          this.showCursor = true;
        }
        this.fullMarkdown += data.content;
        this.$nextTick(() => {
          this.parseAndRender(this.fullMarkdown);
        });
      }
    },

    // ---------- 思考步骤定时器 ----------
    startThinkingTimer() {
      this.stopThinkingTimer();
      this.thinkingTimer = setInterval(() => {
        if (this.firstContentReceived) {
          this.stopThinkingTimer();
          return;
        }
        const next = this.stepIndex + 1;
        if (next < this.thinkingSteps.length) {
          this.stepIndex = next;
          this.statusTitle = this.thinkingSteps[next].title;
          this.statusDetail = this.thinkingSteps[next].detail;
        } else {
          this.stopThinkingTimer();
        }
      }, 1500);
    },

    stopThinkingTimer() {
      if (this.thinkingTimer) {
        clearInterval(this.thinkingTimer);
        this.thinkingTimer = null;
      }
    },

    // ---------- 解析并更新视图 ----------
    parseAndRender(markdown) {
      if (!markdown || markdown.trim().length === 0) return;
      try {
        const parsed = this.parseMarkdown(markdown);
        if (parsed.overviewContent) this.overviewContent = parsed.overviewContent;
        if (parsed.sections && parsed.sections.length > 0) this.sections = parsed.sections;
        if (parsed.riskSection) this.riskSection = parsed.riskSection;
      } catch (e) {
        console.warn('Markdown 解析失败，保留已有内容', e);
      }
    },

    // ---------- Markdown 解析（保持不变） ----------
    parseMarkdown(markdown) {
      const lines = markdown.split('\n');
      let overviewContent = '';
      const sections = [];
      let riskItems = [];
      let disclaimer = '';

      let currentSection = null;
      let currentItems = [];
      let currentIntro = [];
      let isCollectingItems = false;
      let isRiskSection = false;
      let isOverview = false;

      const validSectionNumbers = ['一', '二', '三', '四', '五', '六'];
      const iconMap = {
        '一': 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-core.svg',
        '二': 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-basic.svg',
        '三': 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-amoled.svg',
        '四': 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-car.svg',
        '五': 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-fund.svg',
        '六': 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-plus.svg',
      };

      const processText = (text) => {
        let content = text;
        content = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        content = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
        return content;
      };

      const isValidSection = (num) => validSectionNumbers.includes(num);

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const riskMatch = line.match(/^#{2,3}\s+(风险提示.*)$/);
        if (riskMatch) {
          if (currentSection && (currentItems.length > 0 || currentIntro.length > 0)) {
            sections.push({
              title: currentSection,
              intro: [...currentIntro],
              items: [...currentItems],
              icon: iconMap[currentSection.charAt(0)] || 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-core.svg',
            });
          }
          currentSection = null;
          currentItems = [];
          currentIntro = [];
          isCollectingItems = false;
          isRiskSection = true;
          isOverview = false;
          continue;
        }

        const sectionMatch = line.match(/^##\s+([一二三四五六七八九十]+)、(.+)$/);
        if (sectionMatch) {
          const num = sectionMatch[1];
          if (!isValidSection(num)) {
            if (currentSection && (currentItems.length > 0 || currentIntro.length > 0)) {
              sections.push({
                title: currentSection,
                intro: [...currentIntro],
                items: [...currentItems],
                icon: iconMap[currentSection.charAt(0)] || 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-core.svg',
              });
            }
            currentSection = null;
            currentItems = [];
            currentIntro = [];
            isCollectingItems = false;
            isRiskSection = false;
            isOverview = false;
            continue;
          }

          if (currentSection && (currentItems.length > 0 || currentIntro.length > 0)) {
            sections.push({
              title: currentSection,
              intro: [...currentIntro],
              items: [...currentItems],
              icon: iconMap[currentSection.charAt(0)] || 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-core.svg',
            });
          }
          currentSection = `${sectionMatch[1]}、${sectionMatch[2]}`;
          currentItems = [];
          currentIntro = [];
          isCollectingItems = true;
          isRiskSection = false;
          isOverview = false;
          continue;
        }

        if (line.includes('信息仅供参考')) {
          disclaimer = line;
          continue;
        }

        if (!isOverview && !isRiskSection && !currentSection && line && !line.match(/^#/)) {
          overviewContent = processText(line);
          isOverview = true;
          continue;
        }

        if (isRiskSection) {
          const bulletMatch = line.match(/^-\s+(.+)$/);
          if (bulletMatch) {
            riskItems.push(processText(bulletMatch[1]));
            continue;
          }
          const numMatch = line.match(/^(\d+)\.\s+(.+)$/);
          if (numMatch) {
            riskItems.push(processText(numMatch[2]));
            continue;
          }
          if (line && !line.match(/^#/)) {
            if (riskItems.length > 0) {
              const last = riskItems[riskItems.length - 1];
              if (!/[。；;！?！]$/.test(last)) {
                riskItems[riskItems.length - 1] = last + ' ' + processText(line);
                continue;
              }
            }
            riskItems.push(processText(line));
          }
          continue;
        }

        if (isCollectingItems && currentSection) {
          const bulletMatch = line.match(/^-\s+(.+)$/);
          if (bulletMatch) {
            currentItems.push(processText(bulletMatch[1]));
            continue;
          }
          const numMatch = line.match(/^(\d+)\.\s+(.+)$/);
          if (numMatch) {
            currentItems.push(processText(numMatch[2]));
            continue;
          }
          if (line && !line.match(/^#/) && !line.match(/^-\s+/) && !line.match(/^\d+\.\s+/)) {
            const processed = processText(line);
            if (currentItems.length === 0) {
              currentIntro.push(processed);
            } else {
              const last = currentItems[currentItems.length - 1];
              if (!/[。；;！?！]$/.test(last)) {
                currentItems[currentItems.length - 1] = last + ' ' + processed;
              }
            }
          }
        }
      }

      if (currentSection && (currentItems.length > 0 || currentIntro.length > 0)) {
        sections.push({
          title: currentSection,
          intro: [...currentIntro],
          items: [...currentItems],
          icon: iconMap[currentSection.charAt(0)] || 'https://cdncos.aigupiao.com/assets/pc/img/2023/figma-assets/icon-core.svg',
        });
      }

      return {
        overviewContent,
        sections,
        riskSection: riskItems.length > 0 ? { title: '风险提示', items: riskItems } : null,
        disclaimer,
      };
    },

    // ---------- 切换风险卡片 ----------
    toggleRisk() {
      this.isRiskOpen = !this.isRiskOpen;
    },

    // ---------- 清理 ----------
    cleanup() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.stopThinkingTimer();
    },

    // ---------- 重新生成 ----------
    reGenerate() {
      this.startStream();
    },
  },
};
</script>

<style scoped>
/* ========== 原样式（保持兼容） ========== */
.section-stack {
  display: grid;
  gap: 8px;
}

.overview,
.logic-card,
.risk-card {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(90, 30, 30, 0.06);
}

.overview {
  display: grid;
  gap: 8px;
  padding: 12px;
  background: #ffffff;
}
.overview h2 {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  font-weight: 800;
}
.overview p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  font-weight: 400;
  word-break: break-word;
}
.overview a {
  color: #1d88d0;
  text-decoration: none;
}
.overview strong {
  color: #333;
  font-weight: 800;
}

.logic-card,
.risk-card {
  overflow: hidden;
  background: #ffffff;
}
details {
  display: block;
}
summary {
  min-height: 64px;
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 22px;
  gap: 12px;
  align-items: center;
  padding: 12px;
  list-style: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
summary::-webkit-details-marker {
  display: none;
}
.icon {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  align-self: center;
  border-radius: 9px;
  background: #fdecec;
  color: #ff8a8a;
}
.icon img {
  width: 20px;
  height: 20px;
  display: block;
}
.summary-title {
  margin: 0;
  min-width: 0;
  color: #333;
  font-size: 15px;
  line-height: 1.6;
  font-weight: 800;
  word-break: break-word;
}
.chevron {
  width: 9px;
  height: 9px;
  justify-self: center;
  border-right: 2px solid #9aa3b2;
  border-bottom: 2px solid #9aa3b2;
  transform: rotate(45deg);
  transition: transform 220ms ease;
}
details[open] .chevron {
  transform: rotate(225deg);
}
.logic-body {
  padding: 0 12px 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  word-break: break-word;
  overflow: hidden;
  transition: height 240ms ease, opacity 200ms ease;
  will-change: height, opacity;
}
.logic-body p {
  margin: 0 0 8px;
}
.logic-body p:last-child {
  margin-bottom: 0;
}
.numbered-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 6px;
  align-items: start;
}
.item-marker {
  font-weight: 400;
  white-space: nowrap;
}
.item-copy {
  min-width: 0;
}

.risk-card {
  display: grid;
  gap: 12px;
  padding: 12px;
  background: #fffaf4;
}
.risk-head {
  width: 100%;
  border: 0;
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 22px;
  gap: 12px;
  align-items: center;
  padding: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  font: inherit;
  -webkit-tap-highlight-color: transparent;
}
.risk-head .icon {
  background: #feedc5;
  color: #ffb84c;
}
.risk-head h3 {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  font-weight: 800;
}
.risk-card.open .risk-head .chevron {
  transform: rotate(225deg);
}
.risk-list {
  display: grid;
  gap: 8px;
  overflow: hidden;
  transition: height 240ms ease, opacity 200ms ease;
  will-change: height, opacity;
}
.risk-item {
  margin: 0;
  color: #8a4b0a;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}
.risk-divider {
  height: 1px;
  background: repeating-linear-gradient(90deg, rgba(235, 162, 81, 0.18) 0 3px, transparent 3px 6px);
}

/* ========== 思考动效（初始状态） ========== */
.thinking-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 12px;
  background: #f8f9fb;
  border-radius: 12px;
  margin-bottom: 12px;
}
.thinking-wrapper .thinking-spinner {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  border: 3px solid #e0e0e0;
  border-top: 3px solid #1d88d0;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
.thinking-wrapper .thinking-content {
  flex: 1;
  min-width: 0;
}
.thinking-wrapper .thinking-title {
  font-weight: 600;
  font-size: 15px;
  color: #333;
  line-height: 1.4;
}
.thinking-wrapper .thinking-detail {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-top: 2px;
}
.thinking-wrapper .thinking-dots {
  display: flex;
  gap: 4px;
  margin-top: 6px;
}
.thinking-wrapper .thinking-dots span {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #999;
  animation: dot-bounce 1.4s ease-in-out infinite both;
}
.thinking-wrapper .thinking-dots span:nth-child(2) {
  animation-delay: 0.2s;
}
.thinking-wrapper .thinking-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dot-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* ========== 打字机光标 ========== */
.cursor {
  display: inline-block;
  color: #fde5e5;
  font-weight: 300;
  font-size: 1.2em;
  animation: blink 0.8s step-end infinite;
  margin-left: 2px;
}
@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

/* ========== 错误 ========== */
.stream-error {
  text-align: center;
  padding: 20px 0;
}
.stream-error p {
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 12px;
}
.stream-error button {
  padding: 6px 20px;
  border: none;
  border-radius: 6px;
  background: #1d88d0;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}
.stream-error button:hover {
  background: #166ba3;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>